import { AllPreference } from '@@types/Preference';

import { LOCALE, NEW_LINE_MANNER, NOTIFICATION_RANGE } from '~/constants';
import { PreferenceCategory } from '~/constants/member';
import { globalSettingStore } from '~/stores/GlobalSettingStore';
import { api } from '~/stores/providers/Api2';
import { getPreferenceByCategory, makePreferenceFormat } from '~/utils/settingUtil';
import { isEmpty } from 'lodash';
import { append, pipe, tap, when } from 'ramda';
import mergeRight from 'ramda/es/mergeRight';

class SettingActions {
  private isNewLineEmpty = pipe(getPreferenceByCategory(PreferenceCategory.NEW_LINE_MANNER), isEmpty);
  private isNotificationRangeEmpty = pipe(getPreferenceByCategory(PreferenceCategory.NOTIFICATION_RANGE), isEmpty);
  private isThreadEmpty = pipe(getPreferenceByCategory(PreferenceCategory.THREAD), isEmpty);

  public fetchUserPreferences = () => {
    return api.me
      .fetchUserPreferences()
      .then(pipe(this.fixDefaultPreferences, tap(globalSettingStore.setUserPreferences)));
  };

  public setNewLine = (newLineManner: string) => {
    const params = [
      {
        category: PreferenceCategory.NEW_LINE_MANNER,
        value: { newLineManner },
      },
    ];
    return api.me.updateUserPreferences(params);
  };

  public updateTranslationLang = (lang: LOCALE) => {
    const params = [makePreferenceFormat('lang', lang)];
    return api.me.updateUserPreferences(params);
  };

  public updateNotification = (notification) => {
    const params = [
      {
        category: PreferenceCategory.NOTIFICATION,
        value: mergeRight(globalSettingStore.userPreferences[PreferenceCategory.NOTIFICATION] ?? {}, {
          [PreferenceCategory.NOTIFICATION]: notification,
        }),
      },
    ];
    return api.me.updateUserPreferences(params);
  };

  public updateForcePush(value) {
    const params = [
      {
        category: PreferenceCategory.FORCE_PUSH,
        value: mergeRight(globalSettingStore.userPreferences[PreferenceCategory.FORCE_PUSH] ?? {}, {
          [PreferenceCategory.FORCE_PUSH]: value,
        }),
      },
    ];
    return api.me.updateUserPreferences(params);
  }

  public updateNotificationRange = (notificationRange) => {
    const params = [makePreferenceFormat('notificationRange', notificationRange)];
    return api.me.updateUserPreferences(params);
  };

  public updateCoachMark = (coachMark: Record<string, unknown>) => {
    return api.me.updateUserPreferences([{ category: PreferenceCategory.COACH_MARK, value: coachMark }]);
  };

  public updateClickToOpenThread = (value: boolean) => {
    return api.me.updateUserPreferences([{ category: PreferenceCategory.THREAD, value: { clickToOpen: value } }]);
  };

  private appendPreference = (category, defaultValue, preferences) => {
    return append(makePreferenceFormat(category, defaultValue), preferences);
  };

  private fixNewLine = (preferences) => {
    return this.appendPreference(PreferenceCategory.NEW_LINE_MANNER, NEW_LINE_MANNER.SHIFT_ENTER, preferences);
  };

  private fixNotificationRange = (preferences) => {
    return this.appendPreference(PreferenceCategory.NOTIFICATION_RANGE, NOTIFICATION_RANGE.ALL, preferences);
  };

  private fixThread = (preferences) => {
    return append({ category: PreferenceCategory.THREAD, value: { clickToOpen: true } }, preferences);
  };

  private fixDefaultPreferences = (preferences: AllPreference[]): AllPreference[] => {
    return pipe(
      when(this.isNewLineEmpty, this.fixNewLine),
      when(this.isNotificationRangeEmpty, this.fixNotificationRange),
      when(this.isThreadEmpty, this.fixThread)
    )(preferences);
  };
}

export default SettingActions;
